/**
 * ProfilePage — the student's account.
 *
 *   NOT signed in → a "Continue with Google" card.
 *   Signed in     → the account: photo, name, email (from Google, read-only),
 *                   editable mobile, campus, and sign-out. Everything is saved
 *                   to the database via /customers/me.
 */

import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Camera,
  Receipt,
  MapPin,
  HelpCircle,
  ChevronRight,
  LogOut,
  GraduationCap,
} from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Card, Button, Spinner } from '@/components/ui';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { useCollege } from '@/context/CollegeContext';
import { fileToResizedDataUrl } from '@/utils/image';
import { ROUTES } from '@/constants/routes';

const MENU = [
  { label: 'My Orders', description: 'Track and reorder', icon: Receipt, to: ROUTES.orders },
  { label: 'Saved Addresses', description: 'Hostel & block', icon: MapPin, to: ROUTES.addresses },
  { label: 'Help & Support', description: 'Get assistance', icon: HelpCircle, to: ROUTES.help },
];

export function ProfilePage() {
  const { status, customer, logout, updateProfile } = useCustomerAuth();

  if (status === 'loading') {
    return (
      <Container className="grid min-h-[60vh] place-items-center">
        <Spinner size="lg" className="text-primary-600" />
      </Container>
    );
  }

  if (status !== 'authenticated') return <SignedOut />;

  return <SignedIn customer={customer} logout={logout} updateProfile={updateProfile} />;
}

/* --------------------------- signed-out view ---------------------------- */

function SignedOut() {
  const navigate = useNavigate();

  return (
    <Container className="max-w-md py-10">
      <Card className="p-8 text-center">
        <img
          src="/brand/rocket07-mark.png"
          alt="Rocket07"
          width={64}
          height={64}
          className="mx-auto mb-4 h-16 w-16 object-contain"
        />
        <h1 className="text-xl font-extrabold text-ink">Sign in to your account</h1>
        <p className="mt-1.5 text-sm text-muted">
          Save your details, addresses and orders across devices.
        </p>
        <div className="mt-6 flex justify-center">
          {/* Same first-time rule as the login screen. */}
          <GoogleSignInButton
            onSignedIn={({ isNew } = {}) => {
              if (isNew) navigate(ROUTES.profileDetails);
            }}
          />
        </div>
      </Card>

      <div className="mt-6 overflow-hidden rounded-xl border border-line bg-surface shadow-card">
        {MENU.map((item, index) => (
          <Link
            key={item.label}
            to={item.to}
            className={`flex items-center gap-4 p-4 hover:bg-canvas ${index !== 0 ? 'border-t border-line' : ''}`}
          >
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-canvas text-ink">
              <item.icon size={20} />
            </span>
            <span className="flex-1 text-sm font-semibold text-ink">{item.label}</span>
            <ChevronRight size={18} className="text-muted" />
          </Link>
        ))}
      </div>
    </Container>
  );
}

/* ---------------------------- signed-in view ---------------------------- */

function SignedIn({ customer, logout, updateProfile }) {
  const fileRef = useRef(null);
  const [error, setError] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  /**
   * The campus being browsed. Prefer the context — that is the one this
   * session actually chose — and fall back to the account for the moment
   * right after sign-in, before the colleges list has loaded.
   */
  const { college } = useCollege();
  const campusName = college?.name ?? customer?.college?.name ?? '';

  const onPickAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarBusy(true);
    setError(null);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 256);
      await updateProfile({ avatar: dataUrl });
    } catch (err) {
      setError(err.message ?? 'Could not update photo.');
    } finally {
      setAvatarBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Container className="max-w-2xl py-6">
      {/* Identity */}
      <Card className="flex items-center gap-4 p-5">
        <div className="relative">
          <span className="grid h-20 w-20 place-items-center overflow-hidden rounded-full bg-primary-50 text-primary-600">
            {customer.avatar ? (
              <img
                src={customer.avatar}
                alt="Profile"
                referrerPolicy="no-referrer"
                className="h-full w-full object-cover"
              />
            ) : (
              <User size={34} />
            )}
          </span>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            aria-label="Change photo"
            className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-full border-2 border-white bg-primary-600 text-white hover:bg-primary-700"
          >
            {avatarBusy ? <Spinner size="sm" className="text-white" /> : <Camera size={15} />}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onPickAvatar}
            className="hidden"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-bold text-ink">{customer.name || 'Student'}</p>
          <p className="truncate text-sm text-muted">{customer.email}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={logout} leftIcon={<LogOut size={15} />}>
          Sign out
        </Button>
      </Card>

      {/*
        Always on screen, directly under the Sign out button, because signing
        out IS the way to change campus and nothing else on this page says so.
        A student who picked the wrong college would otherwise be left looking
        for a setting that deliberately does not exist.

        Not a dismissible tooltip on the button: it would be missed by exactly
        the person who needs it, and it answers a question ("which campus am I
        even in?") worth having permanently visible.
      */}
      <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-accent-200 bg-accent-100/60 px-3.5 py-3">
        <GraduationCap size={18} className="mt-0.5 shrink-0 text-accent-600" />
        <p className="text-xs leading-relaxed text-ink">
          {campusName && (
            <>
              You&apos;re shopping <span className="font-bold">{campusName}</span>.{' '}
            </>
          )}
          <span className="text-muted">
            To change campus, tap <span className="font-semibold text-ink">Sign out</span> and sign
            in again.
          </span>
        </p>
      </div>

      {error && (
        <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{error}</div>
      )}

      <Link
        to={ROUTES.profileDetails}
        className="mt-4 flex items-center gap-4 rounded-xl border border-line bg-surface p-4 shadow-card hover:bg-canvas"
      >
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary-50 text-primary-600">
          <User size={20} />
        </span>
        <span className="flex-1">
          <span className="block text-sm font-semibold text-ink">Profile Details</span>
          <span className="text-xs text-muted">Name, email and mobile number</span>
        </span>
        <ChevronRight size={18} className="text-muted" />
      </Link>

      {/* Account menu */}
      <div className="mt-4 overflow-hidden rounded-xl border border-line bg-surface shadow-card">
        {MENU.map((item, index) => (
          <Link
            key={item.label}
            to={item.to}
            className={`flex items-center gap-4 p-4 hover:bg-canvas ${index !== 0 ? 'border-t border-line' : ''}`}
          >
            <span className="grid h-10 w-10 place-items-center rounded-lg bg-canvas text-ink">
              <item.icon size={20} />
            </span>
            <span className="flex-1">
              <span className="block text-sm font-semibold text-ink">{item.label}</span>
              <span className="text-xs text-muted">{item.description}</span>
            </span>
            <ChevronRight size={18} className="text-muted" />
          </Link>
        ))}
      </div>
    </Container>
  );
}

export default ProfilePage;
